import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  deleteField,
  type Unsubscribe,
} from '@firebase/firestore';
import { getFunctions, httpsCallable } from '@firebase/functions';
import { db, app } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { StructureClaim, StakeholderType, PostVisibility, EvaluationStatus } from '../types';
import type { NotifyAgentResult } from '../types/api';
import { mapDoc, mapDocs } from '@utils/firestore';
import { AppError } from '@utils/errorHandler';
import { StructureClaimSchema } from '../schemas/firestoreSchemas';

export const structureClaimsApi = {
  async getClaim(structureId: string): Promise<StructureClaim | null> {
    const ref = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const snap = await getDoc(ref);
    return mapDoc(snap, StructureClaimSchema);
  },

  subscribeToClaim(
    structureId: string,
    onChange: (claim: StructureClaim | null) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const ref = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    return onSnapshotWithRetry(
      ref,
      (snap) => onChange(snap.exists() ? mapDoc(snap, StructureClaimSchema) : null),
      (err) => onError?.(err)
    );
  },

  async getClaimedStructures(userId: string, limitCount = 50): Promise<StructureClaim[]> {
    const q = query(
      collection(db, COLLECTIONS.STRUCTURE_CLAIMS),
      where('claimedBy', '==', userId),
      orderBy('claimedAt', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return mapDocs(snap, StructureClaimSchema);
  },

  /**
   * Live version of getClaimedStructures — same constraint shape (no new
   * composite index needed) so the map's claim-coloring state can react to
   * this user's own claim/unclaim writes (and claims made on another
   * device) without waiting on the CF-derived claimSummaries baseline.
   */
  subscribeToUserClaims(
    userId: string,
    onChange: (claims: StructureClaim[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, COLLECTIONS.STRUCTURE_CLAIMS),
      where('claimedBy', '==', userId),
      orderBy('claimedAt', 'desc'),
      limit(50)
    );
    return onSnapshotWithRetry(
      q,
      (snap) => onChange(mapDocs(snap, StructureClaimSchema)),
      (err) => onError?.(err)
    );
  },

  async claimStructure(
    structureId: string,
    userId: string,
    serviceAreaId: string,
    options?: { insurerChecklistId?: string; stakeholderType?: StakeholderType }
  ): Promise<void> {
    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const existing = await getDoc(claimRef);

    if (existing.exists()) {
      const data = existing.data();
      if (data.claimedBy !== userId) {
        throw new AppError('Structure is already claimed by another user', 'PERMISSION_DENIED');
      }
    }

    const claimData: Record<string, unknown> = {
      claimedBy: userId,
      claimedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      serviceAreaId,
    };
    if (options?.insurerChecklistId !== undefined) {
      claimData.insurerChecklistId = options.insurerChecklistId;
    }
    if (options?.stakeholderType !== undefined) {
      claimData.stakeholderType = options.stakeholderType;
    }
    await setDoc(claimRef, claimData);
  },

  async unclaimStructure(structureId: string, userId: string): Promise<void> {
    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const existing = await getDoc(claimRef);

    if (!existing.exists()) {
      throw new AppError('No claim found for this structure', 'NOT_FOUND');
    }
    if (existing.data().claimedBy !== userId) {
      throw new AppError('You can only unclaim structures you own', 'PERMISSION_DENIED');
    }

    await deleteDoc(claimRef);
  },

  async updateInsuranceAssignment(
    structureId: string,
    userId: string,
    assignment: { insurerChecklistId?: string | null; stakeholderType?: StakeholderType | null }
  ): Promise<void> {
    // If the caller is assigning a checklist (not clearing it), confirm the
    // checklist doc actually exists — without this, a claimant could point
    // at a non-existent or another insurer's template, which breaks the
    // report-threshold trigger and muddies audit trails.
    if (assignment.insurerChecklistId) {
      const checklistSnap = await getDoc(
        doc(db, COLLECTIONS.INSURANCE_CHECKLISTS, assignment.insurerChecklistId)
      );
      if (!checklistSnap.exists()) {
        throw new AppError('Insurance checklist not found', 'NOT_FOUND');
      }
    }

    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const existing = await getDoc(claimRef);

    if (!existing.exists()) {
      throw new AppError('No claim found for this structure', 'NOT_FOUND');
    }
    if (existing.data().claimedBy !== userId) {
      throw new AppError('You can only update structures you own', 'PERMISSION_DENIED');
    }

    // null means "back to the default model" — stored claims encode that as
    // field ABSENCE, so it must be deleteField() and not a literal null.
    // StructureClaimSchema types both fields as z.string().optional(), which
    // rejects null; mapDoc drops any doc that fails to parse, so writing null
    // here made the claim read back as null and the property looked unclaimed.
    // Same sentinel handling as postsApi.updatePost.
    await updateDoc(claimRef, {
      ...(assignment.insurerChecklistId !== undefined && {
        insurerChecklistId: assignment.insurerChecklistId ?? deleteField(),
      }),
      ...(assignment.stakeholderType !== undefined && {
        stakeholderType: assignment.stakeholderType ?? deleteField(),
      }),
      updatedAt: serverTimestamp(),
    });
  },

  // Recipient emails are PII, and the claim doc is readable by every authed
  // user (the map layer needs it) — so the list lives in an owner-only
  // subdoc: structureClaims/{id}/private/recipients. The report Cloud
  // Function reads it via admin SDK.
  async getReportRecipients(structureId: string): Promise<string[]> {
    const snap = await getDoc(
      doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId, 'private', 'recipients')
    );
    const raw = snap.data()?.recipients;
    return Array.isArray(raw) ? raw.filter((r): r is string => typeof r === 'string') : [];
  },

  async updateReportRecipients(
    structureId: string,
    userId: string,
    recipients: string[]
  ): Promise<void> {
    // Recipient list is read by the report-send Cloud Function and handed
    // straight to Resend. Validate here so bad data never reaches Firestore;
    // the rule also caps list size, and buildAndSendReport re-validates.
    const MAX_RECIPIENTS = 10;
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!Array.isArray(recipients)) {
      throw new AppError('recipients must be an array', 'VALIDATION_ERROR');
    }
    if (recipients.length > MAX_RECIPIENTS) {
      throw new AppError(`At most ${MAX_RECIPIENTS} recipients`, 'VALIDATION_ERROR');
    }
    const clean: string[] = [];
    const seen = new Set<string>();
    for (const raw of recipients) {
      if (typeof raw !== 'string') {
        throw new AppError('Recipient must be a string', 'VALIDATION_ERROR');
      }
      const trimmed = raw.trim();
      if (trimmed.length === 0 || trimmed.length > 320 || !EMAIL_RE.test(trimmed)) {
        throw new AppError(`Invalid recipient email: ${raw}`, 'VALIDATION_ERROR');
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      clean.push(trimmed);
    }

    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const existing = await getDoc(claimRef);

    if (!existing.exists()) {
      throw new AppError('No claim found for this structure', 'NOT_FOUND');
    }
    if (existing.data().claimedBy !== userId) {
      throw new AppError('You can only update structures you own', 'PERMISSION_DENIED');
    }

    await setDoc(doc(claimRef, 'private', 'recipients'), {
      recipients: clean,
      updatedAt: serverTimestamp(),
    });
  },

  async updateDefaultPostVisibility(
    structureId: string,
    userId: string,
    visibility: PostVisibility
  ): Promise<void> {
    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    const existing = await getDoc(claimRef);

    if (!existing.exists()) {
      throw new AppError('No claim found for this structure', 'NOT_FOUND');
    }
    if (existing.data().claimedBy !== userId) {
      throw new AppError('You can only update structures you own', 'PERMISSION_DENIED');
    }

    await updateDoc(claimRef, {
      defaultPostVisibility: visibility,
      updatedAt: serverTimestamp(),
    });
  },

  async updateProgressSnapshot(
    structureId: string,
    snapshot: { completed: number; total: number }
  ): Promise<void> {
    const claimRef = doc(db, COLLECTIONS.STRUCTURE_CLAIMS, structureId);
    await updateDoc(claimRef, {
      progressSnapshot: {
        completed: snapshot.completed,
        total: snapshot.total,
        updatedAt: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * "Notify my agent": delegates to the `notifyAgent` callable function,
   * which writes the claim's evaluationStatus/lastAgentNotification (owner
   * only, checked server-side) and emails everyone with visibility into the
   * property. See functions/src/agents/notifyAgent.ts.
   */
  async notifyAgent(
    structureId: string,
    status: EvaluationStatus,
    note?: string
  ): Promise<NotifyAgentResult> {
    const functions = getFunctions(app);
    const fn = httpsCallable<
      { structureId: string; status: EvaluationStatus; note?: string },
      NotifyAgentResult
    >(functions, 'notifyAgent');
    const { data } = await fn({ structureId, status, note });
    return data;
  },
};
